import useOverlay from "@/hooks/common/useOverlay";
import {useTaggingClick} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {
	FlagGoodsBrandCd,
	FlagGoodsCd,
	FlagGoodsTmember,
	FlagGoodsTp,
	FlagPromotion,
} from "@COMPONENTS/common/flag/Desktop";
import {StartWeek, airOrderSort, hashTagManufacture} from "@COMPONENTS/common/goodsUtil";
import SharePopup from "@COMPONENTS/common/modal/share/Desktop";
import EventListPopup from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import * as style from "@COMPONENTS/style/productlist/productlist";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {IconButton} from "@FRAMEWORK/ui/desktop/button";
import {StarIcon} from "@FRAMEWORK/ui/desktop/flag";
import AirlineIcon from "@FRAMEWORK/ui/desktop/icon/AirlineIcon";
import {Checkbox} from "@FRAMEWORK/ui/desktop/input";
import {CompareButtonBox, CompareTextBox, Flex} from "@FRAMEWORK/ui/desktop/layout";
import {StackList, StackListItem} from "@FRAMEWORK/ui/desktop/list";
import ProductListThumbnail from "@FRAMEWORK/ui/desktop/thumbnail/ProductListThumbnail";
import {GoodsByMenu} from "@QUERIES/prdt/goods/useGoodsByMenu";
import {goodsAvailEventAsync} from "@QUERIES/prdt/goods/useGoodsOneAvailEvent";
import {Flag} from "@yb/ui/desktop/common/Flag";
import {Noimage} from "@yb/ui/desktop/common/Noimage";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import {useRouter} from "next/router";
import React, {useState} from "react";

const HashTagDescription = (props: GoodsByMenu) => {
	if (!props.keyWordList) return <></>;
	return (
		<style.Hashtag>
			<span>{hashTagManufacture(props.keyWordList, 3)}</span>
		</style.Hashtag>
	);
};

const TagSquareDescription = (props: GoodsByMenu) => (
	<>
		<style.TagSquareWrap gap="5">
			<FlagGoodsCd goodsCd={props.goodsCd} />
			<FlagGoodsTp goodsTp={props.goodsTp} />
			<FlagGoodsBrandCd goodsBrandCd={props.goodsBrandCd} />
			<FlagGoodsTmember goodsTmember={props.goodsTmember} />
		</style.TagSquareWrap>
	</>
);

const InfoGroupDescription = (props: GoodsByMenu) => {
	const {goodsFeaturesList, goodsTpointContents} = props;
	if (!goodsFeaturesList && !goodsTpointContents) return <></>;

	const [isShow, setIsShow] = useState(false);
	const benefitToggle = e => {
		setIsShow(true);
		e.preventDefault();
	};

	const goodsFeatureString = goodsFeaturesList
		?.split("|")
		.map(v => v.split(",")[0])
		.join(", ")
		.toString();

	return (
		<style.InfoGroup direction="column">
			<style.InfoGroupContent show={isShow}>
				{goodsFeatureString && <em>{goodsFeatureString}</em>}
				{goodsTpointContents && (
					<div dangerouslySetInnerHTML={{__html: goodsTpointContents.replace(/\n/g, "<br>")}}></div>
				)}

				{goodsTpointContents && goodsTpointContents.match(/\n/g)?.length > 2 && !isShow && (
					<style.BenefitButton type="button" onClick={benefitToggle}>
						<span>더보기</span>
					</style.BenefitButton>
				)}
			</style.InfoGroupContent>
		</style.InfoGroup>
	);
};

const StartFixDateDescription = (props: GoodsByMenu) => {
	if (!props.startFixOpenDtList) return <></>;
	const formatDateList = props.startFixOpenDtList.split(",").map(date => dayjs(date).format("MM.DD"));
	return (
		<dl>
			<dt>출발확정</dt>
			<dd>
				<span>{formatDateList.slice(0, 10).join(" / ")}</span>
			</dd>
		</dl>
	);
};

const StartEndDateDescription = (props: GoodsByMenu) => (
	<>
		<dl>
			<dt>출발기간</dt>
			<dd>{`${dayjs(props.minStartDt).format("YYYY.MM.DD")} ~ ${dayjs(props.maxStartDt).format("YYYY.MM.DD")}`}</dd>
		</dl>
	</>
);

const StartWeekDayDescription = (props: GoodsByMenu) => (
	<>
		<dt>출발요일</dt>
		<dd>
			<span>{StartWeek(props)}</span>
		</dd>
	</>
);

const AirDescription = (props: GoodsByMenu) => {
	if (!props.airCd) return <></>;
	const companyList = airOrderSort(props);
	const trArray = companyList.slice(0, 4);
	const otherCdCnt = companyList.length - 4;
	return (
		<dl>
			<dt>이용교통</dt>
			{trArray.map((trString, index) => {
				const trInfoArray = trString.split(",");
				return (
					<dd key={index}>
						<AirlineIcon
							img={`${publicRuntimeConfig.ENV_STATIC_CDN_URL}/images/air/${trInfoArray[1]}.png`}
							title={trInfoArray[0]}
						/>
					</dd>
				);
			})}
			<dd>{otherCdCnt > 0 && ` 외 ${otherCdCnt}`}</dd>
		</dl>
	);
};

const PriceDescription = (props: GoodsByMenu) => (
	<>
		<Flex align="center">
			<style.PriceTag>
				{props.goodsPromotion && <style.PromotionFlag>{props.goodsPromotion.split(",")[1]}</style.PromotionFlag>}
				{props.couponYn === "Y" && <Flag $type="promotion" $flag="coupon" text="쿠폰할인" />}
			</style.PriceTag>
			{props?.ypdYn === "Y" && (
				<>
					<style.RegularPrice>{format.koKRFormatNumber(props.ypdPrice)}원</style.RegularPrice>
				</>
			)}
			<style.DiscountPrice>
				<strong>{format.koKRFormatNumber(props.minPrice)}</strong> 원~
			</style.DiscountPrice>
		</Flex>
	</>
);

type ProductType = {
	product: GoodsByMenu;
	checkedArray?: Array<string>;
	handleChecked?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	handleCompare?: () => void;
};

const LocalGoodsComponent = (props: ProductType) => {
	const {product, checkedArray, handleChecked, handleCompare} = props;
	const {goodsCd} = product;
	const router = useRouter();
	const dspSid = `${router.query?.dspSid?.slice(0, -2)}00` as string;
	const menu = router.query?.menu as string;

	const isChecked = checkedArray.includes(goodsCd);
	const lastChecked = checkedArray[checkedArray.length - 1];

	// 공유하기
	const [open] = useOverlay();
	const handleShare = () => {
		open(prop => <SharePopup {...prop} parameter={`menu=${menu}&dspSid=${dspSid}&goodsCd=${goodsCd}`} />);
	};

	// 출발일 보기
	const [slide] = useOverlay("slideIn");
	const handleEventList = async e => {
		const result = await goodsAvailEventAsync(goodsCd);
		const {body} = result.data;
		slide(prop => <EventListPopup {...prop} goodsCd={goodsCd} availEvent={body} />);
	};

	const {taggingClick} = useTaggingClick("pkg_select_prod_list", {goodsCd});

	return (
		<>
			<style.ProcutList>
				<style.ProcutListItem>
					<style.ProcutItemLink>
						<ProductListThumbnail>
							<style.Checkboxbox>
								<Checkbox id="check" onChange={handleChecked} checked={isChecked} bg="yellow" value={goodsCd} />
							</style.Checkboxbox>
							<style.PromotionBox>
								{product.ypdYn === "Y" && <Flag $type="promotion" $flag="yellowpangdeal" text="옐로팡딜" />}
								<FlagPromotion goodsCd={goodsCd} />
							</style.PromotionBox>
							<style.ImageBox>
								<Noimage src={product.imageThum3} alt="" width="275px" height="185px" />
								{/* hashTag */}
								<HashTagDescription {...product} />
								{/* hashTag */}
							</style.ImageBox>
						</ProductListThumbnail>
						<Flex direction="column">
							<Flex justify="space-between" align="start">
								{/* TagSquare */}
								<TagSquareDescription {...product} />
								{/* //TagSquare */}
								<StackList>
									<StackListItem>
										<style.Review>
											<StarIcon />
											<style.Grade>{product.rvwAvgScore || "-"}</style.Grade>&nbsp;/ 5
										</style.Review>
									</StackListItem>
									<StackListItem>
										<IconButton icon="share" onClick={handleShare}>
											<span>공유하기</span>
										</IconButton>
									</StackListItem>
								</StackList>
							</Flex>
							<style.Name>{product.goodsNm}</style.Name>
							{/* InfoGroup */}
							<InfoGroupDescription {...product} />
							{/* //InfoGroup */}
							<style.Summary>
								{/* 출발확정 */}
								<StartFixDateDescription {...product} />
								<Flex gap="10">
									{/* 출발기간 */}
									<StartEndDateDescription {...product} />
									<style.SummaryDays>
										{/* 출발요일 */}
										<StartWeekDayDescription {...product} />
									</style.SummaryDays>
								</Flex>
								{/* 이용교통 */}
								<AirDescription {...product} />
							</style.Summary>
							<style.PriceBox>
								{/* price */}
								<PriceDescription {...product} />
							</style.PriceBox>
						</Flex>
					</style.ProcutItemLink>
					<style.ProductListItemData
						onClick={e => {
							handleEventList(e);
							taggingClick();
						}}
					/>

					{lastChecked === goodsCd && checkedArray.length > 1 && <CompareButtonBox onClick={handleCompare} />}
					{isChecked && (lastChecked !== goodsCd || checkedArray.length === 1) && <CompareTextBox />}
				</style.ProcutListItem>
			</style.ProcutList>
		</>
	);
};

const LocalGoods = (props: ProductType) => (
	<ErrorBoundary logMsg="Product Error">
		<LocalGoodsComponent {...props} />
	</ErrorBoundary>
);

export default LocalGoods;
